import { useState } from 'react';
import { toast } from 'sonner';

import type { FaxTransmission } from '@/services/api/faxService';

interface UseDownloadFaxResult {
  downloadFax: (transmission: FaxTransmission) => Promise<void>;
  isDownloading: boolean;
}

/**
 * Hook for downloading fax transmission files
 * Handles the download process with loading states and error handling
 */
export const useDownloadFax = (): UseDownloadFaxResult => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFax = async (transmission: FaxTransmission): Promise<void> => {
    // Validate that the transmission has a file URL
    if (!transmission.file_url) {
      toast.error('Download URL not available for this transmission');
      return;
    }

    // Only allow downloads for sent or delivered transmissions
    if (transmission.status !== 'sent' && transmission.status !== 'delivered') {
      toast.error(
        'Downloads are only available for sent or delivered transmissions',
      );
      return;
    }

    setIsDownloading(true);

    try {
      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = transmission.file_url;

      // Use the file_name from the transmission, or generate a default name
      const fileName =
        transmission.file_name ||
        `fax_${transmission.transmission_id}_${
          new Date(transmission.created_at).toISOString().split('T')[0]
        }.pdf`;

      link.download = fileName;

      // Set attributes for better browser compatibility
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Downloading ${fileName}`);
    } catch {
      toast.error('Failed to download fax transmission');
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadFax,
    isDownloading,
  };
};
